import React, { useState } from 'react';
import CollectionSearch from '../search/CollectionSearch';
import { filterRecords } from '../../utils/search';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import StoreIcon from '@mui/icons-material/Store';

const IRS_CATEGORIES = [
  'Cloud Infrastructure',
  'AI Services',
  'Software & SaaS',
  'Telecommunications',
  'Business Insurance',
  'Office Equipment',
  'Legal & Professional',
];

function VendorRegistryView({ vendors, onUpdate }) {
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [search, setSearch] = useState('');

  const handleExpand = (vendor) => {
    if (editingId === vendor.id) {
      setEditingId(null);
      setEditFields({});
    } else {
      setEditingId(vendor.id);
      setEditFields({
        vendor_name: vendor.vendor_name || '',
        default_category: vendor.default_category || '',
        default_business_pct: vendor.default_business_pct != null ? vendor.default_business_pct : '',
        pct_rationale: vendor.pct_rationale || '',
        default_business_purpose_brief: vendor.default_business_purpose_brief || '',
        default_business_purpose_formal: vendor.default_business_purpose_formal || '',
        teller_patterns: vendor.teller_patterns || '',
        retrieval_steps: vendor.retrieval_steps || '',
        default_disposition: vendor.default_disposition || 'claim',
      });
    }
  };

  const handleSave = async (vendorId) => {
    await onUpdate(vendorId, editFields);
    setEditingId(null);
    setEditFields({});
  };

  const claimVendors = vendors.filter(v => v.default_disposition !== 'exclude');
  const excludeVendors = vendors.filter(v => v.default_disposition === 'exclude');
  const visibleVendors = filterRecords(vendors, search, vendor => [
    vendor.vendor_name, vendor.default_category, vendor.teller_patterns, vendor.default_disposition,
    vendor.default_business_purpose_brief, vendor.default_business_purpose_formal, vendor.retrieval_steps,
    vendor.is_recurring ? 'recurring' : 'one time', vendor.notes,
  ], { rank: true });

  const renderVendor = (vendor) => (
    <Accordion
      key={vendor.id}
      expanded={editingId === vendor.id}
      onChange={() => handleExpand(vendor)}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {vendor.vendor_name}
          </Typography>
          <Chip label={vendor.default_category || 'Uncategorized'} size="small" variant="outlined" color="primary" />
          {vendor.default_business_pct != null && (
            <Chip label={`${vendor.default_business_pct}%`} size="small" variant="outlined" />
          )}
          {vendor.is_recurring && (
            <Chip label="Recurring" size="small" variant="outlined" color="success" />
          )}
          {vendor.default_disposition === 'exclude' && (
            <Chip label="Excluded" size="small" variant="outlined" color="error" />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {editingId === vendor.id && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Vendor Name"
              value={editFields.vendor_name}
              onChange={(e) => setEditFields({ ...editFields, vendor_name: e.target.value })}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={editFields.default_category}
                onChange={(e) => setEditFields({ ...editFields, default_category: e.target.value })}
                label="Category"
              >
                {IRS_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
                <MenuItem value="Personal">Personal (excluded)</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Business Use %"
                type="number"
                value={editFields.default_business_pct}
                onChange={(e) => setEditFields({ ...editFields, default_business_pct: e.target.value })}
                size="small"
                sx={{ width: 150 }}
                inputProps={{ min: 0, max: 100 }}
              />
              <TextField
                label="Rationale"
                value={editFields.pct_rationale}
                onChange={(e) => setEditFields({ ...editFields, pct_rationale: e.target.value })}
                size="small"
                fullWidth
              />
            </Box>
            <TextField
              label="Brief Purpose"
              value={editFields.default_business_purpose_brief}
              onChange={(e) => setEditFields({ ...editFields, default_business_purpose_brief: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
            <TextField
              label="Formal Purpose (IRS)"
              value={editFields.default_business_purpose_formal}
              onChange={(e) => setEditFields({ ...editFields, default_business_purpose_formal: e.target.value })}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
            <TextField
              label="Teller Patterns (pipe-separated)"
              value={editFields.teller_patterns}
              onChange={(e) => setEditFields({ ...editFields, teller_patterns: e.target.value })}
              fullWidth
              size="small"
              helperText="e.g., CLAUDE.AI|ANTHROPIC — used to auto-match charges"
            />
            <TextField
              label="Receipt Retrieval Steps"
              value={editFields.retrieval_steps}
              onChange={(e) => setEditFields({ ...editFields, retrieval_steps: e.target.value })}
              fullWidth
              size="small"
              helperText="e.g., claude.ai > Settings > Billing > Payment history"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Disposition</InputLabel>
              <Select
                value={editFields.default_disposition}
                onChange={(e) => setEditFields({ ...editFields, default_disposition: e.target.value })}
                label="Disposition"
              >
                <MenuItem value="claim">Claim (business expense)</MenuItem>
                <MenuItem value="exclude">Exclude (personal)</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<SaveIcon />}
                onClick={() => handleSave(vendor.id)}
              >
                Save Changes
              </Button>
              <Button size="small" onClick={() => { setEditingId(null); setEditFields({}); }}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <StoreIcon color="success" />
        <Typography variant="h6">Recurring Vendor Registry</Typography>
        <Chip label={`${vendors.length} vendors`} size="small" variant="outlined" />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These defaults auto-fill when a matching charge appears on your Chase business card.
        Click any vendor to edit its defaults.
      </Typography>

      {/* Claimed vendors */}
      <CollectionSearch label="Search vendor registry" value={search} onChange={setSearch}
        total={vendors.length} shown={visibleVendors.length} />
      {search && !visibleVendors.length && <Typography>No matching vendors.</Typography>}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Business Vendors ({claimVendors.length})</Typography>
      {visibleVendors.filter(v => v.default_disposition !== 'exclude').map(renderVendor)}

      {/* Excluded vendors */}
      {excludeVendors.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Excluded (Personal) ({excludeVendors.length})</Typography>
          {visibleVendors.filter(v => v.default_disposition === 'exclude').map(renderVendor)}
        </Box>
      )}
    </Box>
  );
}

export default VendorRegistryView;
